import { runInAction } from "mobx";
import { inject, observer } from "mobx-react";
import React from "react";
import { useTranslation } from "react-i18next";
import { match } from "react-router";
import { Spacer } from "src/components";
import { Tab } from "src/components/tab";
import { Text } from "src/components/text";
import { MatchParams } from "src/routes";
import { DeliverableStore, ItemStores, QuestionStore } from "src/stores";
import { DataType, ItemType, QuestionPosition, DeliverablePosition } from "src/types";
import { Searchbar } from "src/views/searchbar";
import styled from "styled-components";
import { AdvSearchBar } from "./advSearchBar";

export interface IPosFilterProps {
  questionStore?: QuestionStore;
  delivStore?:    DeliverableStore;
  routeInfo?:     match<MatchParams>;
  type:           ItemType;
  itemStores?:    ItemStores;
}

export const PosFilter = inject("questionStore", "delivStore", "itemStores")(observer((props: IPosFilterProps) => {
  const { delivStore, questionStore, type, itemStores } = props;
  const { t, i18n } = useTranslation();
  const [tabId, setTabId] = React.useState<number | 'all'>(0);
  const [advFilters, setShowAdvFilters] = React.useState<boolean>(false);

  if (![DataType.Question, DataType.Deliverable].includes(type)) return null;
  React.useEffect(() => {
    setShowAdvFilters(false);
    delivStore.resetFilterVales();
    questionStore.resetFilterVales();
    runInAction(() => {
      questionStore.advSearch = false;
      delivStore.advSearch    = false;
    })
  }, [type]);

  React.useEffect(() => {
    if (delivStore.advSearch || questionStore.advSearch) {
      setShowAdvFilters(true)
    }
  }, [delivStore.advSearch, questionStore.advSearch]);

  if (type === DataType.Question) {
    return (
      <Container>
        {!questionStore.advSearch &&
          <TabsContainer>
            <Text size="small" weight="bold">{t("placing")}</Text>
            <Spacer size="xxsmall" />
            <Tab key={"all"}
              active  = {tabId === "all"}
              onClick = {() => {
                questionStore.setFilterValue("position", [], true);
                setTabId("all");
                questionStore.updateQuery();
              }}>{t("all")}</Tab>
            {Object.values(QuestionPosition).map((p, idx) => (
              <Tab key={p}
                count   = {questionStore.positionCounts[p] || 0}
                active  = {tabId === idx}
                onClick = {() => {
                  questionStore.setFilterValue("position", [{ key: p }], true);
                  setTabId(idx);
                  questionStore.updateQuery();
                }}>{t(p)}</Tab>
            ))}
          </TabsContainer>
        }

        <>
          {advFilters === true && <AdvSearchBar type={DataType.Question} />}
          <Searchbar
            advButton   = {true}
            placeholder = {t("question_placeholder")}
            onChange    = {(val) => itemStores[type].setSearchString(val, true)}
            toggleAdv   = {() => {
              setShowAdvFilters(!advFilters);
              questionStore.toggleAdvSearch();
              if (itemStores.question.advSearch) itemStores.deliverable.linkOptions.load();
              else itemStores.deliverable.linkOptions.clear();
            }}
          />
        </>
      </Container>
    );
  } else { // type === DataType.Deliverable
    return (
      <Container>
        {!delivStore.advSearch &&
          <TabsContainer>
            <Text size="small" weight="bold">{t("placing")}</Text>
            <Spacer size="small" />
            <Tab key={"all"}
              active  = {tabId === "all"}
              onClick = {() => {
                delivStore.setFilterValue("all", [], true);
                setTabId("all");
                delivStore.updateQuery();
              }}>{t("all")}</Tab>
            {Object.values(DeliverablePosition).map((p, idx) => (
              <Tab key={p}
                active  = {tabId === idx}
                onClick = {() => {
                  switch (p) {
                    case DeliverablePosition.Board: 
                      delivStore.setFilterValue('done', [{key: false}], false)
                      delivStore.setFilterValue('delivered', [{key: false}], false)
                      break;
                    case DeliverablePosition.Delivered: 
                      delivStore.setFilterValue('done', [{key: false}], false)
                      delivStore.setFilterValue('delivered', [{key: true}], false)
                      break;
                    case DeliverablePosition.Done: 
                      delivStore.setFilterValue(p, [{ key: true }], false);
                      break;
                    default: break;
                  }
                  setTabId(idx);
                  delivStore.updateQuery();
                }}>{t(p)}</Tab>
            ))}
          </TabsContainer>
        }
        {type === DataType.Deliverable &&
          <>
            {advFilters === true && <AdvSearchBar type={DataType.Deliverable} />}
            <Searchbar
              advButton   = {true}
              placeholder = {type === DataType.Deliverable && t("deliverable_placeholder")}
              onChange    = {(val) => itemStores[type].setSearchString(val, true)}
              toggleAdv   = {() => {
                itemStores[type].toggleAdvSearch();
                setShowAdvFilters(true);
                if (type === DataType.Deliverable && itemStores.question.advSearch) itemStores.deliverable.linkOptions.load();
                else if (type === DataType.Deliverable) itemStores.deliverable.linkOptions.clear();
              }}
            />
          </>
        }
      </Container>
    );
  }
}));

const Container = styled.div`
  display:         flex;
  justify-content: space-between;
  align-items:     flex-start;
  flex-wrap:       wrap;
  align-items:     center;
  margin:          0 3rem 1rem;
  gap:             1rem;
`;

const TabsContainer = styled.div`
  display:         flex;
  flex-wrap:       wrap;
  justify-content: flex-start;
  align-items:     center;
  gap:             1rem;
`